import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CreditCard, Plus, Edit, Trash2, RefreshCw, Check, X, Loader2, Crown, Video, Heart, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { apiClient } from '../../lib/api-client';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

interface PlanFeatures {
  unlimitedMatches: boolean;
  contactDetails: boolean;
  chatUnlimited: boolean;
  prioritySupport: boolean;
  profileBoost: {
    count: number;
    daysPerBoost: number;
  };
  profileHighlight: boolean;
  advancedFilters: boolean;
  horoscopeMatching: boolean;
  dedicatedRM: boolean;
  videoCall: boolean;
  dailyVideoCallLimit: number;
  totalContactViewLimit: number; // Total contact views (one-time allocation, not daily)
  maxChats: number;
  dailyInterestLimit: number;
  totalSuperInterests: number; // One-time credits (not daily)
}

interface Plan {
  _id: string;
  name: string;
  slug: string;
  price: number;
  duration: number;
  description: string;
  features: PlanFeatures;
  isActive: boolean;
  isPopular: boolean;
  displayOrder: number;
  discount: number;
  finalPrice: number;
}

import { useAppDispatch, useAppSelector } from '../../store/store';
import { fetchAdminPlans } from '../../store/slices/adminSlice';

export function AdminPlans() {
  const dispatch = useAppDispatch();
  const { plans: plansState } = useAppSelector(state => state.admin);
  const { list: plans, loading } = plansState;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    duration: 30,
    description: '',
    discount: 0,
    finalPrice: 0,
    displayOrder: 0,
    isActive: true,
    isPopular: false,
    unlimitedMatches: false,
    contactDetails: false,
    chatUnlimited: false,
    prioritySupport: false,
    profileBoostCount: 0,
    profileBoostDays: 0,
    profileHighlight: false,
    advancedFilters: false,
    horoscopeMatching: false,
    dedicatedRM: false,
    videoCall: false,
    dailyVideoCallLimit: 0,
    totalContactViewLimit: 0, // Total contact views (one-time allocation, not daily)
    maxChats: 0,
    dailyInterestLimit: 10,
    totalSuperInterests: 0 // One-time credits (not daily)
  });

  useEffect(() => {
    dispatch(fetchAdminPlans());
  }, [dispatch]);

  const syncSubscriptionFeatures = async () => {
    try {
      const response = await apiClient.post('/admin/sync-subscription-features');
      toast.success(response.data.message || 'Subscription features synced successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to sync subscription features');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
        price: formData.price,
        duration: formData.duration,
        description: formData.description,
        discount: formData.discount,
        isPopular: formData.isPopular,
        features: {
          unlimitedMatches: formData.unlimitedMatches,
          contactDetails: formData.contactDetails,
          chatUnlimited: formData.chatUnlimited,
          prioritySupport: formData.prioritySupport,
          profileBoost: {
            count: formData.profileBoostCount,
            daysPerBoost: formData.profileBoostDays
          },
          profileHighlight: formData.profileHighlight,
          advancedFilters: formData.advancedFilters,
          horoscopeMatching: formData.horoscopeMatching,
          dedicatedRM: formData.dedicatedRM,
          videoCall: formData.videoCall,
          dailyVideoCallLimit: formData.dailyVideoCallLimit,
          totalContactViewLimit: formData.totalContactViewLimit, // Total contact views (one-time allocation, not daily)
          maxChats: formData.maxChats,
          dailyInterestLimit: formData.dailyInterestLimit,
          totalSuperInterests: formData.totalSuperInterests // One-time credits (not daily)
        },
        isActive: formData.isActive,
        displayOrder: formData.displayOrder,
        finalPrice: formData.finalPrice
      };

      if (editingPlan) {
        await apiClient.put(`/plans/${editingPlan._id}`, payload);
        toast.success('Plan updated successfully');
      } else {
        await apiClient.post('/plans', payload);
        toast.success('Plan created successfully');
      }

      setIsDialogOpen(false);
      setEditingPlan(null);
      resetForm();
      dispatch(fetchAdminPlans());
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);

    // Debug logging (only in development)
    if (import.meta.env.MODE === 'development') {
      console.log('Editing plan:', plan);
      console.log('Plan features:', plan.features);
    }

    // Populate form data synchronously before opening dialog
    setFormData({
      name: plan.name,
      price: plan.price,
      duration: plan.duration,
      description: plan.description,
      discount: plan.discount || 0,
      finalPrice: plan.finalPrice || 0,
      displayOrder: plan.displayOrder || 0,
      isActive: plan.isActive,
      isPopular: plan.isPopular || false,
      unlimitedMatches: plan.features?.unlimitedMatches || false,
      contactDetails: plan.features?.contactDetails || false,
      chatUnlimited: plan.features?.chatUnlimited || false,
      prioritySupport: plan.features?.prioritySupport || false,
      profileBoostCount: plan.features?.profileBoost?.count || 0,
      profileBoostDays: plan.features?.profileBoost?.daysPerBoost || 0,
      profileHighlight: plan.features?.profileHighlight || false,
      advancedFilters: plan.features?.advancedFilters || false,
      horoscopeMatching: plan.features?.horoscopeMatching || false,
      dedicatedRM: plan.features?.dedicatedRM || false,
      videoCall: plan.features?.videoCall || false,
      // Handle limits properly - check for undefined/null vs 0
      dailyVideoCallLimit: plan.features?.dailyVideoCallLimit !== undefined ? plan.features.dailyVideoCallLimit : 0,
      totalContactViewLimit: plan.features?.totalContactViewLimit !== undefined ? plan.features.totalContactViewLimit : 0, // Total contact views (one-time allocation, not daily)
      maxChats: plan.features?.maxChats !== undefined ? plan.features.maxChats : 0,
      dailyInterestLimit: plan.features?.dailyInterestLimit !== undefined ? plan.features.dailyInterestLimit : 10,
      totalSuperInterests: plan.features?.totalSuperInterests !== undefined ? plan.features.totalSuperInterests : 0 // One-time credits (not daily)
    });

    // Open dialog after setting data
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!planToDelete) return;

    try {
      await apiClient.delete(`/plans/${planToDelete}`);
      toast.success('Plan deleted successfully');
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      dispatch(fetchAdminPlans());
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: 0,
      duration: 30,
      description: '',
      discount: 0,
      finalPrice: 0,
      displayOrder: 0,
      isActive: true,
      isPopular: false,
      unlimitedMatches: false,
      contactDetails: false,
      chatUnlimited: false,
      prioritySupport: false,
      profileBoostCount: 0,
      profileBoostDays: 0,
      profileHighlight: false,
      advancedFilters: false,
      horoscopeMatching: false,
      dedicatedRM: false,
      videoCall: false,
      dailyVideoCallLimit: 0,
      totalContactViewLimit: 0, // Total contact views (one-time allocation, not daily)
      maxChats: 0,
      dailyInterestLimit: 10,
      totalSuperInterests: 0 // One-time credits (not daily)
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="size-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
            <p className="text-gray-500">Manage user subscription plans</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => dispatch(fetchAdminPlans())}>
            <RefreshCw className="size-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingPlan(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="size-4 mr-2" />
                Create Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" key={editingPlan?._id || 'new'}>
              <DialogHeader>
                <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
                <DialogDescription>
                  {editingPlan ? 'Update the plan details below' : 'Fill in the details to create a new subscription plan'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="features">Features</TabsTrigger>
                    <TabsTrigger value="limits">Limits & Quotas</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Plan Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="price">Price (₹) *</Label>
                        <Input
                          id="price"
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                          required
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="duration">Duration (days) *</Label>
                        <Input
                          id="duration"
                          type="number"
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                          required
                          min="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="discount">Discount (%)</Label>
                        <Input
                          id="discount"
                          type="number"
                          value={formData.discount}
                          onChange={(e) => {
                            const discount = Number(e.target.value);
                            const calculatedFinalPrice = Math.round(formData.price - (formData.price * discount / 100));
                            setFormData({ ...formData, discount, finalPrice: calculatedFinalPrice });
                          }}
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="displayOrder">Display Order</Label>
                        <Input
                          id="displayOrder"
                          type="number"
                          value={formData.displayOrder}
                          onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
                          min="0"
                        />
                      </div>
                    </div>
                    {/* Final Price Manual Override */}
                    <div className="space-y-2">
                      <Label htmlFor="finalPrice">Final Price (₹) - Manual Override</Label>
                      <Input
                        id="finalPrice"
                        type="number"
                        value={formData.finalPrice}
                        onChange={(e) => setFormData({ ...formData, finalPrice: Number(e.target.value) })}
                        min="0"
                      />
                      <p className="text-xs text-gray-500">
                        Leave as 0 to auto-calculate from discount, or set manually to override
                      </p>
                    </div>
                    {/* Final Price Display */}
                    {formData.discount > 0 && formData.finalPrice === 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Auto-Calculated Final Price:</span>
                          <span className="text-xl font-bold text-green-600">
                            ₹{Math.round(formData.price - (formData.price * formData.discount / 100))}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Original Price: ₹{formData.price} - {formData.discount}% = ₹{Math.round(formData.price - (formData.price * formData.discount / 100))}
                        </p>
                      </div>
                    )}
                    {formData.finalPrice > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">Final Price (Manual Override):</span>
                          <span className="text-xl font-bold text-blue-600">
                            ₹{formData.finalPrice}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This price will be used instead of auto-calculation
                        </p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="isActive">Active Status</Label>
                        <Switch
                          id="isActive"
                          checked={formData.isActive}
                          onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="isPopular">Mark as Popular</Label>
                        <Switch
                          id="isPopular"
                          checked={formData.isPopular}
                          onCheckedChange={(checked) => setFormData({ ...formData, isPopular: checked })}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="features" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="unlimitedMatches" className="cursor-pointer">Unlimited Matches</Label>
                          <p className="text-sm text-gray-500">Browse unlimited profiles</p>
                        </div>
                        <Switch
                          id="unlimitedMatches"
                          checked={formData.unlimitedMatches}
                          onCheckedChange={(checked) => setFormData({ ...formData, unlimitedMatches: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="contactDetails" className="cursor-pointer">Contact Details</Label>
                          <p className="text-sm text-gray-500">View phone numbers</p>
                        </div>
                        <Switch
                          id="contactDetails"
                          checked={formData.contactDetails}
                          onCheckedChange={(checked) => setFormData({ ...formData, contactDetails: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="chatUnlimited" className="cursor-pointer">Unlimited Chat</Label>
                          <p className="text-sm text-gray-500">No message limits</p>
                        </div>
                        <Switch
                          id="chatUnlimited"
                          checked={formData.chatUnlimited}
                          onCheckedChange={(checked) => setFormData({ ...formData, chatUnlimited: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="videoCall" className="cursor-pointer">Video Calling</Label>
                          <p className="text-sm text-gray-500">Enable video calls</p>
                        </div>
                        <Switch
                          id="videoCall"
                          checked={formData.videoCall}
                          onCheckedChange={(checked) => setFormData({ ...formData, videoCall: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="profileHighlight" className="cursor-pointer">Profile Highlight</Label>
                          <p className="text-sm text-gray-500">Stand out in search</p>
                        </div>
                        <Switch
                          id="profileHighlight"
                          checked={formData.profileHighlight}
                          onCheckedChange={(checked) => setFormData({ ...formData, profileHighlight: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="advancedFilters" className="cursor-pointer">Advanced Filters</Label>
                          <p className="text-sm text-gray-500">More search options</p>
                        </div>
                        <Switch
                          id="advancedFilters"
                          checked={formData.advancedFilters}
                          onCheckedChange={(checked) => setFormData({ ...formData, advancedFilters: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="horoscopeMatching" className="cursor-pointer">Horoscope Matching</Label>
                          <p className="text-sm text-gray-500">Kundli compatibility</p>
                        </div>
                        <Switch
                          id="horoscopeMatching"
                          checked={formData.horoscopeMatching}
                          onCheckedChange={(checked) => setFormData({ ...formData, horoscopeMatching: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="prioritySupport" className="cursor-pointer">Priority Support</Label>
                          <p className="text-sm text-gray-500">24/7 priority help</p>
                        </div>
                        <Switch
                          id="prioritySupport"
                          checked={formData.prioritySupport}
                          onCheckedChange={(checked) => setFormData({ ...formData, prioritySupport: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <Label htmlFor="dedicatedRM" className="cursor-pointer">Dedicated RM</Label>
                          <p className="text-sm text-gray-500">Personal relationship manager</p>
                        </div>
                        <Switch
                          id="dedicatedRM"
                          checked={formData.dedicatedRM}
                          onCheckedChange={(checked) => setFormData({ ...formData, dedicatedRM: checked })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label htmlFor="profileBoostCount">Profile Boost Count</Label>
                        <Input
                          id="profileBoostCount"
                          type="number"
                          value={formData.profileBoostCount}
                          onChange={(e) => setFormData({ ...formData, profileBoostCount: Number(e.target.value) })}
                          min="0"
                        />
                        <p className="text-xs text-gray-500">Number of boosts available</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profileBoostDays">Days Per Boost</Label>
                        <Input
                          id="profileBoostDays"
                          type="number"
                          value={formData.profileBoostDays}
                          onChange={(e) => setFormData({ ...formData, profileBoostDays: Number(e.target.value) })}
                          min="0"
                        />
                        <p className="text-xs text-gray-500">Duration of each boost</p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="limits" className="space-y-4 mt-4">
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500">Set daily/total limits for various features (0 = unlimited)</p>

                      {/* Debug info - only show when editing */}
                      {editingPlan && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-xs font-semibold text-yellow-800 mb-2">Debug Info (Current DB Values):</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-yellow-700">
                            <div>totalSuperInterests: {editingPlan.features?.totalSuperInterests ?? 'undefined'}</div>
                            <div>totalContactViewLimit: {editingPlan.features?.totalContactViewLimit ?? 'undefined'}</div>
                            <div>dailyContactViewLimit (old): {(editingPlan.features as any)?.dailyContactViewLimit ?? 'undefined'}</div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dailyInterestLimit">Daily Interest Limit</Label>
                          <Input
                            id="dailyInterestLimit"
                            type="number"
                            value={formData.dailyInterestLimit}
                            onChange={(e) => setFormData({ ...formData, dailyInterestLimit: Number(e.target.value) })}
                            min="0"
                          />
                          <p className="text-xs text-gray-500">Interests per day (0 = unlimited)</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="totalSuperInterests">Total Super Interests</Label>
                          <Input
                            id="totalSuperInterests"
                            type="number"
                            value={formData.totalSuperInterests}
                            onChange={(e) => setFormData({ ...formData, totalSuperInterests: Number(e.target.value) })}
                            min="0"
                          />
                          <p className="text-xs text-gray-500">Super interests (one-time credits, not daily)</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dailyVideoCallLimit">Daily Video Call Limit</Label>
                          <Input
                            id="dailyVideoCallLimit"
                            type="number"
                            value={formData.dailyVideoCallLimit}
                            onChange={(e) => setFormData({ ...formData, dailyVideoCallLimit: Number(e.target.value) })}
                            min="-1"
                          />
                          <p className="text-xs text-gray-500">Video calls per day (-1 = unlimited, 0 = no access)</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="totalContactViewLimit">Total Contact View Limit</Label>
                          <Input
                            id="totalContactViewLimit"
                            type="number"
                            value={formData.totalContactViewLimit}
                            onChange={(e) => setFormData({ ...formData, totalContactViewLimit: Number(e.target.value) })}
                            min="-1"
                          />
                          <p className="text-xs text-gray-500">Contact views (one-time allocation, not daily) (-1 = unlimited, 0 = no access)</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="maxChats">Max Chats</Label>
                          <Input
                            id="maxChats"
                            type="number"
                            value={formData.maxChats}
                            onChange={(e) => setFormData({ ...formData, maxChats: Number(e.target.value) })}
                            min="0"
                          />
                          <p className="text-xs text-gray-500">Maximum active chats (0 = unlimited)</p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsDialogOpen(false);
                    setEditingPlan(null);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="size-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan._id} className={plan.isPopular ? 'border-purple-500 border-2' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle>{plan.name}</CardTitle>
                    {plan.isPopular && <Crown className="size-5 text-purple-600" />}
                  </div>
                  <div className="flex gap-2">
                    {plan.isActive ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                    )}
                  </div>
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-bold text-purple-600">₹{plan.finalPrice || plan.price}</div>
                    {plan.discount > 0 && (
                      <div className="text-lg text-gray-400 line-through">₹{plan.price}</div>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{plan.duration} days</div>
                  {plan.discount > 0 && (
                    <Badge className="mt-1 bg-green-100 text-green-700">{plan.discount}% OFF</Badge>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  {plan.features.unlimitedMatches && (
                    <div className="flex items-center gap-2">
                      <Check className="size-4 text-green-600" />
                      <span>Unlimited Matches</span>
                    </div>
                  )}
                  {plan.features.contactDetails && (
                    <div className="flex items-center gap-2">
                      <Check className="size-4 text-green-600" />
                      <span>View Contact Details</span>
                    </div>
                  )}
                  {plan.features.videoCall && (
                    <div className="flex items-center gap-2">
                      <Video className="size-4 text-green-600" />
                      <span>Video Calls {plan.features.dailyVideoCallLimit > 0 ? `(${plan.features.dailyVideoCallLimit}/day)` : '(Unlimited)'}</span>
                    </div>
                  )}
                  {plan.features.dailyInterestLimit > 0 && (
                    <div className="flex items-center gap-2">
                      <Heart className="size-4 text-pink-600" />
                      <span>{plan.features.dailyInterestLimit} Interests/day</span>
                    </div>
                  )}
                  {plan.features.totalSuperInterests > 0 && (
                    <div className="flex items-center gap-2">
                      <Sparkles className="size-4 text-purple-600" />
                      <span>{plan.features.totalSuperInterests} Super Interests (one-time credits)</span>
                    </div>
                  )}
                  {plan.features.chatUnlimited && (
                    <div className="flex items-center gap-2">
                      <MessageCircle className="size-4 text-blue-600" />
                      <span>Unlimited Chat</span>
                    </div>
                  )}
                  {plan.features.profileHighlight && (
                    <div className="flex items-center gap-2">
                      <Check className="size-4 text-green-600" />
                      <span>Profile Highlight</span>
                    </div>
                  )}
                  {plan.features.dedicatedRM && (
                    <div className="flex items-center gap-2">
                      <Check className="size-4 text-green-600" />
                      <span>Dedicated RM</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => handleEdit(plan)}>
                    <Edit className="size-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700"
                    onClick={() => {
                      setPlanToDelete(plan._id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this plan. Users with this plan will not be affected, but new subscriptions to this plan will not be possible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPlanToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}